/*global google */

/**
 * Google place finder class. Using google map js API and google places API
 * @param element
 * @param form
 * @param options
 * @constructor
 */
function GooglePlaceFinder(element, options) {
    this.element = element;
    this.options = options && typeof options != 'undefined' ? options : {};
    this.finderMap = null;
    this.geoTextMapper = null;
    this.autocomplete = null;
    this.getOption = this.getOption.bind(this);
    this.log = this.log.bind(this);
    this.debug = this.debug.bind(this);
    this.setRestriction = this.setRestriction.bind(this);
    this.setGeoLocation = this.setGeoLocation.bind(this);
    this.handleSelection = this.handleSelection.bind(this);
    this.setFinderMap = this.setFinderMap.bind(this);
    this.setGeoTextMapper = this.setGeoTextMapper.bind(this);
    this.setSelectionCallback = this.setSelectionCallback.bind(this);

    this.log('GooglePlaceFinder instance created');
    // Create the autocomplete object, restricting the search predictions to
    // geographical location types.
    this.autocomplete = new google.maps.places.Autocomplete(this.element, {
        types: ['geocode'],
    });

    // Avoid paying for data that you don't need by restricting the set of
    // place fields that are returned to just the address components.
    this.autocomplete.setFields(['address_component', 'geometry.location']);

    // Avoid paying for requests that you don't allow, set allowed countries
    this.setRestriction(this.getOption('allowedCountries', ['uk']));

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    this.autocomplete.addListener(
        'place_changed',
        this.handleSelection.bind(this)
    );

    this.setGeoLocation();
    this.setFinderMap(
        this.getOption('finderMap', {
            /**
             * This is how we are mapping the google address_component to our structure.
             * It is overridable calling setFinderMap method after instance creation.
             */
            street_number: {
                source: 'short_name',
                target: 'address_line_1',
            },
            route: {
                source: 'long_name',
                target: 'address_line_2',
            },
            sublocality: {
                source: 'short_name',
                target: 'address_district',
            },
            locality: {
                source: 'short_name',
                target: 'address_town',
            },
            neighborhood: {
                source: 'short_name',
                target: 'address_neighborhood',
            },
            postal_town: {
                source: 'short_name',
                target: 'address_town',
            },
            administrative_area_level_1: {
                source: 'short_name',
                target: 'address_state',
            },
            administrative_area_level_2: {
                source: 'short_name',
                target: 'address_county',
            },
            country: {
                source: 'short_name',
                target: 'address_country',
            },
            postal_code: {
                source: 'short_name',
                target: 'address_postcode',
            },
            postal_code_prefix: {
                source: 'short_name',
                target: 'address_postcode_prefix',
            },
        })
    );
    this.setGeoTextMapper(
        this.getOption('geoTextMapper', function (mappedObject) {
            /**
             * This is a callback that that creates our geoText (a joined address). Input is the mapped address structure.
             * It is overridable calling setGeoTextMapper method after instance creation.
             * @type {string[]}
             */
            var map = [
                'address_line_1',
                'address_line_2',
                'address_district',
                'address_town',
                'address_country',
            ];
            var address = '';
            var sep = '';
            map.forEach(function (elem) {
                if (typeof mappedObject[elem] !== 'undefined') {
                    address = address + sep + mappedObject[elem];
                    sep = ', ';
                }
            });
            return address;
        })
    );
    this.setSelectionCallback(
        this.getOption('selectionCallback', (selected) => {
            /**
             * input structure:
             * {
             *         'geometry' {
             *                 'location': {
             *                         'lat': fn(),
             *                         'lng': fn()
             *                 }
             *         },
             *         geoText: 'XXXXXXX YYYYYYYYY ZZZZ'
             * }
             */
            // You need to insert the application logic calling setSelectionCallback after creating an instance.
            if (this.debug()) {
                console.log('Selected: ' , selected);
            }
        })
    );
};


/**
 * Returns an option by key
 * @param name
 * @param defaultValue
 * @returns mixed
 */
GooglePlaceFinder.prototype.getOption = function (name, defaultValue) {
    if (typeof defaultValue == 'undefined') {
        defaultValue = null;
    }
    return typeof this.options[name] !== 'undefined'
        ? this.options[name]
        : defaultValue;
};

/**
 * Returns an option value called 'debug'
 * @returns {boolean}
 */
GooglePlaceFinder.prototype.debug = function () {
    return this.getOption('debug', false) === true;
};

/**
 *
 * Writes console log (only in debug mode)
 * @param message
 */
GooglePlaceFinder.prototype.log = function (message) {
    if (this.debug() === true) {
        console.log(message);
    }
};

/**
 * Set finder country restrictions
 * @param countries
 * @returns {GooglePlaceFinder}
 */
GooglePlaceFinder.prototype.setRestriction = function (countries) {
    this.log('Restrictions:');
    this.log(countries);
    this.autocomplete.setComponentRestrictions({
        country: countries,
    });
    return this;
};

/**
 * Sets the map
 * @param map
 * @returns {GooglePlaceFinder}
 */
GooglePlaceFinder.prototype.setFinderMap = function (map) {
    this.finderMap = map;
    return this;
};

/**
 *
 * @param map
 * @returns {googlePlaceFinder}
 */
GooglePlaceFinder.prototype.setGeoTextMapper = function (callback) {
    this.geoTextMapper = callback.bind(this);
    return this;
};

/**
 *
 * @param callback
 * @returns {googlePlaceFinder}
 */
GooglePlaceFinder.prototype.setSelectionCallback = function (callback) {
    this.selectionCallback = callback.bind(this);
    return this;
};

/**
 * Sets the geolocation to google.autocomplete
 * @returns {GooglePlaceFinder}
 */
GooglePlaceFinder.prototype.setGeoLocation = function () {
    if (this.getOption('useGeoLocation', true) === true) {
        this.log('Using geolocation by IP');
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    var geolocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    this.log(geolocation);
                    var circle = new google.maps.Circle({
                        center: geolocation,
                        radius: position.coords.accuracy,
                    });
                    this.autocomplete.setBounds(circle.getBounds());
                }.bind(this)
            );
        }
    }
    return this;
};

/**
 * Returns the selected object
 * @returns {GooglePlaceFinder}
 */
GooglePlaceFinder.prototype.handleSelection = function () {
    // Get the place details from the autocomplete object.
    var place = this.autocomplete.getPlace();
    var geoCodeObj = {};
    var mapping = this.finderMap;

    try {
        this.log('Selected google address_component object');
        this.log(place.address_components);

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.
        for (var i = 0; i < place.address_components.length; i++) {
            for (var x = 0; x < place.address_components[i].types.length; x++) {
                var addressType = place.address_components[i].types[x];
                if (
                    typeof mapping[addressType] !== 'undefined' &&
                    mapping[addressType]
                ) {
                    var val = place.address_components[i][mapping[addressType].source];

                    if (typeof geoCodeObj[mapping[addressType].target] === 'undefined') {
                        geoCodeObj[mapping[addressType].target] = '';
                    }
                    if (geoCodeObj[mapping[addressType].target].length === 0) {
                        geoCodeObj[mapping[addressType].target] = val;
                    }
                }
            }
        }
        this.log('Mapped address object');
        this.log(geoCodeObj);

        var geoCodeText = this.geoTextMapper(geoCodeObj);
        this.log('Selected address (geoText): ' + geoCodeText);
        this.log('LatLng for geocoded place: ' + place.geometry.location);
        this.selectionCallback({
            geometry: {
                location: place.geometry.location,
            },
            geoText: geoCodeText,
        });
    } catch (err) {
        this.log(err);
    }
    return this;
};

export default GooglePlaceFinder;
